import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  TextField,
  Link, 
  Stack,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { 
  Visibility as ViewIcon, 
  Settings as SettingsIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  CheckCircle as ApproveIcon
} from '@mui/icons-material';
import api from '../../services/api';

interface UpdateTokenInfoRequest {
  id: number;
  user_id: number | null;
  update_type: number;
  comment: string;
  token_contract_address: string;
  requester_name: string;
  requester_email_address: string;
  project_name: string;
  official_project_website: string;
  official_project_email_address: string;
  logo: string;
  project_sector: string;
  project_description: string;
  social_profiles: any;
  price_data: any;
  status: number;
  created_at: string;
  updated_at: string;
}

interface UpdateTokenInfoStatusRequest {
  status: number;
}

const UpdateTokenInfoTab: React.FC = () => {
  const [requests, setRequests] = useState<UpdateTokenInfoRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<number>(-1); // -1 means all statuses
  const searchTermRef = useRef(searchTerm);
  
  // Status update dialog
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<UpdateTokenInfoRequest | null>(null);
  const [newStatus, setNewStatus] = useState<number>(0);
  
  // View details dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewRequest, setViewRequest] = useState<UpdateTokenInfoRequest | null>(null);
  
  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRequest, setEditRequest] = useState<UpdateTokenInfoRequest | null>(null);
  const [editForm, setEditForm] = useState({
    project_name: '',
    project_sector: '',
    project_description: '',
    official_project_website: '',
    official_project_email_address: '',
    logo: '',
    social_profiles: {
      twitter: '',
      facebook: '',
      linkedin: '',
      telegram: '',
      discord: '',
      github: '',
      opensea: '',
      medium: '',
      coingecko: '',
      coinmarketcap: ''
    },
    comment: ''
  });
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Approve dialog
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approveRequestData, setApproveRequestData] = useState<UpdateTokenInfoRequest | null>(null);

  // Update ref when searchTerm changes
  useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);

  const statusLabels = {
    0: { label: 'Pending', color: 'warning' as const },
    1: { label: 'In Progress', color: 'info' as const },
    2: { label: 'Completed', color: 'success' as const },
    3: { label: 'Rejected', color: 'error' as const },
    4: { label: 'On Hold', color: 'default' as const },
    5: { label: 'Canceled', color: 'default' as const },
  };

  const updateTypeLabels = {
    0: 'Logo Update',
    1: { label: 'Website Update', color: 'warning' as const },
    2: { label: 'Social Profiles Update', color: 'success' as const },
    3: { label: 'Price Data Update', color: 'secondary' as const },
    4: { label: 'Project Info Update', color: 'info' as const },
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchSubmit = () => {
    setPage(1); // Reset to first page when submitting search
    fetchRequests();
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const handleStatusFilterChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setStatusFilter(value === 'all' ? -1 : parseInt(value));
    setPage(1); // Reset to first page when filtering
  };

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());
      
      if (statusFilter !== -1) {
        params.append('status', statusFilter.toString());
      }
      
      if (searchTermRef.current.trim()) {
        params.append('search', searchTermRef.current.trim());
      }
      
      const queryString = params.toString();
      console.log('Update Token Info Search params:', queryString);
      console.log('Update Token Info Search term:', searchTermRef.current);
      console.log('Update Token Info Status filter:', statusFilter);
      
      const response = await api.get(`/api/inquiry/update-token-info?${queryString}`);
      console.log('Update Token Info Response:', response.data);
      
      // Ensure requests is always an array
      const requestsData = response.data.data.requests || [];
      setRequests(requestsData);
      setTotalPages(response.data.data.pagination.total_pages);
      setError(null);
    } catch (err) {
      console.error('Update Token Info Search error:', err);
      setError('Failed to fetch update token info requests');
      setRequests([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleStatusUpdate = async () => {
    if (!selectedRequest) return;
    
    try {
      const requestData = { status: newStatus };
      console.log('Sending status update request:', requestData);
      console.log('Request URL:', `/api/inquiry/update-token-info/${selectedRequest.id}/status`);
      console.log('Request data type:', typeof requestData.status, 'Value:', requestData.status);
      
      await api.put(`/api/inquiry/update-token-info/${selectedRequest.id}/status`, requestData);
      console.log('Status updated successfully');
      
      setError(null);
      setStatusDialogOpen(false);
      fetchRequests(); // Refresh the list
      
      // Show success notification
      setNotification({
        open: true,
        message: `Status updated to ${getStatusString(newStatus)}`,
        severity: 'success'
      });
    } catch (err: any) {
      console.error('Error updating status:', err);
      console.error('Error response:', err.response);
      console.error('Error response data:', err.response?.data);
      
      if (err.response?.data?.details) {
        setError(`Failed to update status: ${err.response.data.details}`);
      } else if (err.response?.data?.error) {
        setError(`Failed to update status: ${err.response.data.error}`);
      } else {
        setError(`Failed to update status: ${err.message}`);
      }
    }
  };

  const approveRequest = async (request: UpdateTokenInfoRequest) => {
    try {
      console.log('Sending approve request for:', request.id);
      console.log('Request URL:', `/api/inquiry/update-token-info/${request.id}/approve`);
      
      await api.post(`/api/inquiry/update-token-info/${request.id}/approve`);
      console.log('Request approved successfully');
      
      fetchRequests();
      
      // Show success notification
      setNotification({
        open: true,
        message: 'Request approved and changes applied successfully',
        severity: 'success'
      });
    } catch (err: any) {
      console.error('Error approving request:', err);
      console.error('Error response:', err.response);
      console.error('Error response data:', err.response?.data);
      
      if (err.response?.data?.details) {
        setError(`Failed to approve request: ${err.response.data.details}`);
      } else {
        setError('Failed to approve request');
      }
    }
  };

  const handleEditSubmit = async () => {
    if (!editRequest) return;
    
    try {
      // Convert social_profiles object to array format for backend
      const socialProfilesArray = Object.entries(editForm.social_profiles)
        .filter(([_, url]) => url.trim() !== '')
        .map(([platform, url]) => ({
          platform: platform.toLowerCase(),
          url: url.trim()
        }));
      
      const submitData = {
        ...editForm,
        social_profiles: JSON.stringify(socialProfilesArray)
      };
      
      console.log('Sending edit request:', submitData);
      console.log('Request URL:', `/api/inquiry/update-token-info/${editRequest.id}`);
      
      await api.put(`/api/inquiry/update-token-info/${editRequest.id}`, submitData);
      console.log('Request updated successfully');
      
      setError(null);
      setEditDialogOpen(false);
      fetchRequests(); // Refresh the list
    } catch (err: any) {
      console.error('Error updating request:', err);
      console.error('Error response:', err.response);
      
      if (err.response?.data?.details) {
        setError(`Failed to update request: ${err.response.data.details}`);
      } else if (err.response?.data?.error) {
        setError(`Failed to update request: ${err.response.data.error}`);
      } else {
        setError(`Failed to update request: ${err.message}`);
      }
    }
  };

  const openStatusDialog = (request: UpdateTokenInfoRequest) => {
    setSelectedRequest(request);
    setNewStatus(request.status);
    setStatusDialogOpen(true);
  };

  const openEditDialog = (request: UpdateTokenInfoRequest) => {
    setEditRequest(request);
    
    // Parse social_profiles from request
    let parsedSocialProfiles = {
      twitter: '',
      facebook: '',
      linkedin: '',
      telegram: '',
      discord: '',
      github: '',
      opensea: '',
      medium: '',
      coingecko: '',
      coinmarketcap: ''
    };
    
    if (request.social_profiles) {
      try {
        let profiles = request.social_profiles;
        if (typeof profiles === 'string') {
          profiles = JSON.parse(profiles);
        }
        
        // Convert array format to object format
        if (Array.isArray(profiles)) {
          profiles.forEach((profile: any) => {
            if (profile.platform && profile.url) {
              const platform = profile.platform.toLowerCase();
              if (platform in parsedSocialProfiles) {
                (parsedSocialProfiles as any)[platform] = profile.url;
              }
            }
          });
        }
      } catch (error) {
        // Silently handle parsing error
      }
    }
    
    setEditForm({
      project_name: request.project_name,
      project_sector: request.project_sector,
      project_description: request.project_description,
      official_project_website: request.official_project_website,
      official_project_email_address: request.official_project_email_address,
      logo: request.logo,
      social_profiles: parsedSocialProfiles,
      comment: request.comment
    });
    setEditDialogOpen(true);
  };

  const openViewDialog = (request: UpdateTokenInfoRequest) => {
    setViewRequest(request);
    setViewDialogOpen(true);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleEditFormChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (field.includes('.')) {
      // Handle nested fields like social_profiles.twitter
      const [parent, child] = field.split('.');
      setEditForm(prev => ({
        ...prev,
        [parent]: {
          ...(prev as any)[parent],
          [child]: event.target.value
        }
      }));
    } else {
      setEditForm(prev => ({ ...prev, [field]: event.target.value }));
    }
  };



  const getUpdateTypeString = (updateType: number): string => {
    switch (updateType) {
      case 0: return 'Project Info';
      case 1: return 'Logo';
      case 2: return 'Social Profiles';
      case 3: return 'Price Data';
      default: return 'Unknown';
    }
  };

  const getUpdateTypeColor = (updateType: number): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (updateType) {
      case 0: return 'info';      // Project Info - 파란색
      case 1: return 'warning';   // Logo - 주황색
      case 2: return 'success';   // Social Profiles - 초록색
      case 3: return 'secondary'; // Price Data - 보라색
      default: return 'default';
    }
  };

  const getStatusString = (status: number): string => {
    switch (status) {
      case 0: return 'Pending';
      case 1: return 'In Progress';
      case 2: return 'Completed';
      case 3: return 'Rejected';
      case 4: return 'On Hold';
      case 5: return 'Canceled';
      default: return 'Unknown';
    }
  };

  const getStatusColor = (status: number): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case 0: return 'default';
      case 1: return 'info';
      case 2: return 'success';
      case 3: return 'error';
      case 4: return 'warning';
      case 5: return 'secondary';
      default: return 'default';
    }
  };

  const getSocialProfileLinks = (socialProfiles: any) => {
    if (!socialProfiles) return null;
  
    let parsedProfiles = socialProfiles;
    if (typeof socialProfiles === 'string') {
      try {
        parsedProfiles = JSON.parse(socialProfiles);
      } catch {
        return null;
      }
    }
  
    if (!Array.isArray(parsedProfiles) || parsedProfiles.length === 0) {
      return null;
    }
  
    // URL이 있는 것만 필터링하고 플랫폼별로 정렬
    const validProfiles = parsedProfiles
      .filter((p: any) => p.url && p.url.trim() !== '')
      .sort((a: any, b: any) => a.platform.localeCompare(b.platform));
    
    if (validProfiles.length === 0) {
      return (
        <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
          No URLs
        </Typography>
      );
    }
  
    return validProfiles.map((p: any, idx: number) => (
      <Chip
        key={idx}
        label={p.platform.charAt(0).toUpperCase() + p.platform.slice(1)}
        size="small"
        component="a"
        href={p.url}
        target="_blank"
        rel="noopener noreferrer"
        clickable
        sx={{
          mr: 0.5,
          mb: 0.5,
          fontSize: '0.75rem',
          height: '24px',
          '&:hover': {
            backgroundColor: 'primary.light',
            color: 'white'
          }
        }}
      />
    ));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Update Token Info Requests
      </Typography>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Manage token information update requests from project teams and users.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Search and Filter Bar */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          sx={{ flexGrow: 1 }}
          variant="outlined"
          placeholder="Search by requester name, project name, or contract address..."
          value={searchTerm}
          onChange={handleSearch}
          onKeyPress={handleSearchKeyPress}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: (
              <IconButton 
                onClick={handleSearchSubmit}
                size="small"
                sx={{ mr: 1 }}
                title="Search"
              >
                <SearchIcon />
              </IconButton>
            ),
          }}
        />
        
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Status Filter</InputLabel>
          <Select
            value={statusFilter === -1 ? 'all' : statusFilter.toString()}
            onChange={handleStatusFilterChange}
            label="Status Filter"
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value={0}>Pending</MenuItem>
            <MenuItem value={1}>In Progress</MenuItem>
            <MenuItem value={2}>Completed</MenuItem>
            <MenuItem value={3}>Rejected</MenuItem>
            <MenuItem value={4}>On Hold</MenuItem>
            <MenuItem value={5}>Canceled</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Update Type</TableCell>
              <TableCell>Requester</TableCell>
              <TableCell>Project</TableCell>
              <TableCell>Contract Address</TableCell>
              <TableCell>Logo</TableCell>
              <TableCell>Website</TableCell>
              <TableCell>Social Profiles</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests && requests.length > 0 ? (
              requests.map((request) => (
              <TableRow key={request.id}>
                <TableCell>
                  <Chip 
                    label={getUpdateTypeString(request.update_type)} 
                    size="small" 
                    color={getUpdateTypeColor(request.update_type)}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {request.requester_name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {request.requester_email_address}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {request.project_name || 'N/A'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {request.project_sector || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    <a 
                      href={`https://kaiascan.io/token/${request.token_contract_address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#1976d2', textDecoration: 'none' }}
                    >
                      {request.token_contract_address}
                    </a>
                  </Typography>
                </TableCell>
                <TableCell>
                  {request.logo ? (
                    <img 
                      src={request.logo} 
                      alt="Project Logo" 
                      style={{ 
                        width: '40px', 
                        height: '40px', 
                        objectFit: 'cover',
                        borderRadius: '4px',
                        border: '1px solid #e0e0e0'
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.setAttribute('style', 'display: block');
                      }}
                    />
                  ) : (
                    <Box 
                      sx={{ 
                        width: '40px', 
                        height: '40px', 
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      <Typography variant="caption" color="textSecondary">
                        No Logo
                      </Typography>
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  {request.official_project_website ? (
                    <a 
                      href={request.official_project_website}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#1976d2', textDecoration: 'none' }}
                    >
                      {request.official_project_website}
                    </a>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      N/A
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {request.social_profiles ? (
                    <Typography variant="body2" sx={{ maxWidth: 240 }}>
                      <Stack direction="row" flexWrap="wrap" gap={1}>
                        {getSocialProfileLinks(request.social_profiles) || (
                          <Typography variant="body2" color="textSecondary">
                            N/A
                          </Typography>
                        )}
                      </Stack>
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      N/A
                    </Typography>
                  )}
                </TableCell>

                <TableCell>
                  <Chip 
                    label={getStatusString(request.status)} 
                    size="small" 
                    color={getStatusColor(request.status)}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {new Date(request.created_at).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => openViewDialog(request)} size="small" title="View Details">
                    <ViewIcon />
                  </IconButton>
                  <IconButton onClick={() => openEditDialog(request)} size="small" title="Edit Request">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => openStatusDialog(request)} size="small" title="Update Status">
                    <SettingsIcon />
                  </IconButton>
                  <IconButton 
                    onClick={() => {
                      setApproveRequestData(request);
                      setApproveDialogOpen(true);
                    }} 
                    size="small" 
                    title="Approve Request"
                    disabled={request.status !== 0} // Only enable for pending requests
                    sx={{ 
                      color: request.status === 0 ? 'success.main' : 'action.disabled',
                      '&:hover': {
                        color: request.status === 0 ? 'success.dark' : 'action.disabled'
                      }
                    }}
                  >
                    <ApproveIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  {error ? `Error: ${error}` : 'No update token info requests found'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="center" mt={2}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>

      {/* Edit Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Token Info Request</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Project Name"
                  value={editForm.project_name}
                  onChange={handleEditFormChange('project_name')}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Project Sector"
                  value={editForm.project_sector}
                  onChange={handleEditFormChange('project_sector')}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Official Project Website"
                  value={editForm.official_project_website}
                  onChange={handleEditFormChange('official_project_website')}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Official Project Email"
                  value={editForm.official_project_email_address}
                  onChange={handleEditFormChange('official_project_email_address')}
                  margin="normal"
                  type="email"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Logo URL"
                  value={editForm.logo}
                  onChange={handleEditFormChange('logo')}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Project Description"
                  value={editForm.project_description}
                  onChange={handleEditFormChange('project_description')}
                  margin="normal"
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Comment"
                  value={editForm.comment}
                  onChange={handleEditFormChange('comment')}
                  margin="normal"
                  multiline
                  rows={4}
                  placeholder="Additional comments or notes about this request..."
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
                  Social Media Profiles
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Twitter URL"
                  value={editForm.social_profiles.twitter}
                  onChange={handleEditFormChange('social_profiles.twitter')}
                  margin="normal"
                  type="url"
                  placeholder="https://twitter.com/yourproject"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Facebook URL"
                  value={editForm.social_profiles.facebook}
                  onChange={handleEditFormChange('social_profiles.facebook')}
                  margin="normal"
                  type="url"
                  placeholder="https://facebook.com/yourproject"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="LinkedIn URL"
                  value={editForm.social_profiles.linkedin}
                  onChange={handleEditFormChange('social_profiles.linkedin')}
                  margin="normal"
                  type="url"
                  placeholder="https://linkedin.com/company/yourproject"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Telegram URL"
                  value={editForm.social_profiles.telegram}
                  onChange={handleEditFormChange('social_profiles.telegram')}
                  margin="normal"
                  type="url"
                  placeholder="https://t.me/yourproject"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Discord URL"
                  value={editForm.social_profiles.discord}
                  onChange={handleEditFormChange('social_profiles.discord')}
                  margin="normal"
                  type="url"
                  placeholder="https://discord.gg/yourproject"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="GitHub URL"
                  value={editForm.social_profiles.github}
                  onChange={handleEditFormChange('social_profiles.github')}
                  margin="normal"
                  type="url"
                  placeholder="https://github.com/yourproject"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="OpenSea URL"
                  value={editForm.social_profiles.opensea}
                  onChange={handleEditFormChange('social_profiles.opensea')}
                  margin="normal"
                  type="url"
                  placeholder="https://opensea.io/collection/yourproject"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Medium URL"
                  value={editForm.social_profiles.medium}
                  onChange={handleEditFormChange('social_profiles.medium')}
                  margin="normal"
                  type="url"
                  placeholder="https://medium.com/@yourproject"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Coingecko URL"
                  value={editForm.social_profiles.coingecko}
                  onChange={handleEditFormChange('social_profiles.coingecko')}
                  margin="normal"
                  type="url"
                  placeholder="https://www.coingecko.com/en/coins/yourproject"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="CoinMarketCap URL"
                  value={editForm.social_profiles.coinmarketcap}
                  onChange={handleEditFormChange('social_profiles.coinmarketcap')}
                  margin="normal"
                  type="url"
                  placeholder="https://coinmarketcap.com/currencies/yourproject/"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog 
        open={statusDialogOpen} 
        onClose={() => setStatusDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Request Status</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Request:</strong> {selectedRequest?.project_name || selectedRequest?.requester_name}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Current Status:</strong> {selectedRequest ? getStatusString(selectedRequest.status) : ''}
            </Typography>
            
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>New Status</InputLabel>
              <Select
                value={newStatus}
                label="New Status"
                onChange={(e) => setNewStatus(e.target.value as number)}
              >
                <MenuItem value={0}>Pending</MenuItem>
                <MenuItem value={1}>In Progress</MenuItem>
                <MenuItem value={2}>Completed</MenuItem>
                <MenuItem value={3}>Rejected</MenuItem>
                <MenuItem value={4}>On Hold</MenuItem>
                <MenuItem value={5}>Canceled</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleStatusUpdate} variant="contained">
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Request Details</DialogTitle>
        <DialogContent>
          {viewRequest && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Requester Information</Typography>
                  <Typography><strong>Name:</strong> {viewRequest.requester_name}</Typography>
                  <Typography><strong>Email:</strong> {viewRequest.requester_email_address}</Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Project Information</Typography>
                  <Typography><strong>Project Name:</strong> {viewRequest.project_name || 'N/A'}</Typography>
                  <Typography><strong>Sector:</strong> {viewRequest.project_sector || 'N/A'}</Typography>
                  <Typography><strong>Website:</strong> {viewRequest.official_project_website || 'N/A'}</Typography>
                  <Typography><strong>Project Email:</strong> {viewRequest.official_project_email_address || 'N/A'}</Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Project Logo</Typography>
                  {viewRequest.logo ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <img 
                        src={viewRequest.logo} 
                        alt="Project Logo" 
                        style={{ 
                          width: '80px', 
                          height: '80px', 
                          objectFit: 'cover',
                          borderRadius: '8px',
                          border: '1px solid #e0e0e0'
                        }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.nextElementSibling?.setAttribute('style', 'display: block');
                        }}
                      />
                      <Typography variant="body2" color="textSecondary">
                        <a 
                          href={viewRequest.logo} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: '#1976d2', textDecoration: 'none' }}
                        >
                          View Full Size
                        </a>
                      </Typography>
                    </Box>
                  ) : (
                    <Box 
                      sx={{ 
                        width: '80px', 
                        height: '80px', 
                        backgroundColor: '#f5f5f5',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      <Typography variant="caption" color="textSecondary">
                        No Logo
                      </Typography>
                    </Box>
                  )}
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Token Information</Typography>
                  <Typography><strong>Contract Address:</strong> {viewRequest.token_contract_address}</Typography>
                  <Typography><strong>Update Type:</strong> {getUpdateTypeString(viewRequest.update_type)}</Typography>
                  <Typography><strong>Status:</strong> {getStatusString(viewRequest.status)}</Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Description</Typography>
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                    {viewRequest.project_description || 'No description provided'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Comment</Typography>
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                    {viewRequest.comment || 'No comment provided'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Social Profiles</Typography>
                  {viewRequest.social_profiles ? (
                    <Box sx={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem', color: '#333' }}>
                      {JSON.stringify(viewRequest.social_profiles, null, 2)}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary">No social profiles provided.</Typography>
                  )}
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Timestamps</Typography>
                  <Typography><strong>Created:</strong> {new Date(viewRequest.created_at).toLocaleString()}</Typography>
                  <Typography><strong>Updated:</strong> {new Date(viewRequest.updated_at).toLocaleString()}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Approve Confirmation Dialog */}
      <Dialog 
        open={approveDialogOpen} 
        onClose={() => setApproveDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          승인 확인
        </DialogTitle>
        <DialogContent>
          {approveRequestData && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom color="primary">
                다음 요청을 승인하시겠습니까?
              </Typography>
              
              <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom><strong>프로젝트 정보</strong></Typography>
                  <Typography><strong>프로젝트명:</strong> {approveRequestData.project_name}</Typography>
                  <Typography><strong>섹터:</strong> {approveRequestData.project_sector}</Typography>
                  <Typography><strong>설명:</strong> {approveRequestData.project_description}</Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom><strong>연락처 정보</strong></Typography>
                  <Typography><strong>웹사이트:</strong> {approveRequestData.official_project_website}</Typography>
                  <Typography><strong>이메일:</strong> {approveRequestData.official_project_email_address}</Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom><strong>로고</strong></Typography>
                  {approveRequestData.logo ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <img 
                        src={approveRequestData.logo} 
                        alt="Project Logo" 
                        style={{ 
                          width: '100px', 
                          height: '100px', 
                          objectFit: 'cover',
                          borderRadius: '8px',
                          border: '1px solid #e0e0e0'
                        }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                      <Typography variant="body2" color="textSecondary">
                        <a 
                          href={approveRequestData.logo} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: '#1976d2', textDecoration: 'none' }}
                        >
                          전체 크기 보기
                        </a>
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary">로고 없음</Typography>
                  )}
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom><strong>토큰 정보</strong></Typography>
                  <Typography><strong>컨트랙트 주소:</strong> {approveRequestData.token_contract_address}</Typography>
                  <Typography><strong>업데이트 타입:</strong> {getUpdateTypeString(approveRequestData.update_type)}</Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom><strong>소셜 프로필</strong></Typography>
                  {approveRequestData.social_profiles ? (
                    <Box sx={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem', color: '#333', backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                      {JSON.stringify(approveRequestData.social_profiles, null, 2)}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary">소셜 프로필 없음</Typography>
                  )}
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom><strong>코멘트</strong></Typography>
                  <Typography sx={{ whiteSpace: 'pre-wrap', backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                    {approveRequestData.comment || '코멘트 없음'}
                  </Typography>
                </Grid>
              </Grid>
              
              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  승인 시 다음 테이블들이 업데이트됩니다:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ mt: 1, mb: 0 }}>
                  <li>contracts: 로고, 웹사이트, 이메일</li>
                  <li>contract_token_info: 소셜 프로필, 가격 데이터</li>
                  <li>account_name_tags: 이름 태그 (의미있는 경우)</li>
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>취소</Button>
          <Button 
            onClick={() => {
              if (approveRequestData) {
                approveRequest(approveRequestData);
                setApproveDialogOpen(false);
              }
            }} 
            variant="contained" 
            color="primary"
            startIcon={<ApproveIcon />}
          >
            승인
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UpdateTokenInfoTab;
